import React, { memo, ReactElement, ReactNode, useRef, useState } from 'react'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { Box } from '@surglogs-app/ui'
import { DefaultDialog } from '@surglogs-app/ui.dialog'

// @ts-ignore
import { SignaturePadContainer, PAD_WIDTH } from './lib/SignaturePad'
import { useTranslation } from './services'
import { SignaturePadDialogButtons } from './SignaturePadDialogButtons'

const PadContainer = styled(Box)`
  max-width: ${PAD_WIDTH}px;
  margin: 0 auto;
`

interface SignaturePadDialogProps {
  disabled?: boolean
  children?: (props: { component: ReactNode }) => ReactElement
  onConfirm: (data: any) => void
  hideDialog: (value?: unknown) => void
  title?: string
}

export const SignaturePadDialog = memo(
  ({
    disabled,
    children,
    onConfirm,
    hideDialog,
    title,
  }: SignaturePadDialogProps) => {
    const [isEmpty, setIsEmpty] = useState(true)
    const { t } = useTranslation()
    const pad = useRef(SignaturePadContainer())
    const onEnd = () => {
      setIsEmpty(!pad.current.get())
    }
    const padComponent = useRef(pad.current.component({ onEnd }))

    const onConfirmClick = useHandler(() => {
      onConfirm(pad.current.get()?.[0])
      hideDialog()
    })

    const onClearClick = useHandler(() => {
      pad.current.clear()
      setIsEmpty(true)
    })

    const onCancelClick = useHandler(() => {
      hideDialog()
    })

    const isDisabled = disabled != null ? disabled || isEmpty : isEmpty

    return (
      <DefaultDialog
        {...{
          'aria-label': 'signature-dialog',
          title: title || t('title'),
        }}>
        <PadContainer>
          {typeof children === 'function'
            ? children({ component: padComponent.current })
            : padComponent.current}
          <SignaturePadDialogButtons
            {...{
              onConfirmClick,
              onClearClick,
              onCancelClick,
              disabled: isDisabled,
            }}
          />
        </PadContainer>
      </DefaultDialog>
    )
  },
)
